import { ExcelService } from "./excel.service";
import { isStandaloneMode } from "../utils/standalone-mode";

/**
 * Name of the WFI-generated at-a-glance index sheet written by
 * {@link createOrUpdateWIIndex} on Setup save. Exported so anywhere that
 * enumerates workpapers (sync-worksheets → status items, Review page
 * missing-worksheet warning) can exclude it — like the completion
 * certificate, this sheet is WFI meta, not a workpaper.
 */
export const WI_INDEX_SHEET_NAME = "WI_Index";

/**
 * Creates (or rewrites) the WI_Index sheet at the leftmost tab position,
 * showing the workbook's spreadsheet ID (muted grey), client name,
 * preparer, and reviewer — so anyone opening the workbook can see the job
 * headline without navigating to the Setup page.
 *
 * Called from Setup's Save handler when the "Create WI Index sheet"
 * checkbox is on. Idempotent — every save regenerates the sheet so the
 * values stay in sync with the latest Setup state.
 *
 * No-op in standalone mode (no Excel to write to).
 */
export async function createOrUpdateWIIndex(info: {
  spreadsheetId: string;
  clientName?: string;
  balanceDate?: string;
  preparerName?: string;
  reviewerName?: string;
}): Promise<void> {
  if (isStandaloneMode()) return;

  const sheetName = WI_INDEX_SHEET_NAME;

  // Fail fast on a workbook we can't write to, rather than half-applying
  // and leaving a partially-built index sheet behind.
  await ExcelService.assertWorkbookWritable("the WI Index sheet");

  try {
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      let sheet: Excel.Worksheet;

      // Reuse the existing sheet on re-save; else create fresh. Mirrors
      // the completion-certificate reset pattern (unprotect → clear →
      // rewrite) so we don't accumulate stale formatting.
      //
      // getItemOrNullObject rather than getItem + catch: getItem throws
      // on sync, and a blanket catch can't distinguish "sheet doesn't
      // exist" from "sheet exists but is protected" — the latter fell
      // through to add() and failed again on the name collision.
      const existing = sheets.getItemOrNullObject(sheetName);
      existing.load("isNullObject");
      await context.sync();

      if (existing.isNullObject) {
        sheet = sheets.add(sheetName);
        await context.sync();
      } else {
        sheet = existing;
        sheet.visibility = Excel.SheetVisibility.visible;
        try {
          sheet.protection.unprotect();
          sheet.getRange().clear(Excel.ClearApplyTo.all);
          await context.sync();
        } catch (protectionError) {
          console.error("Could not clear the existing WI Index sheet:", protectionError);
          throw new Error(
            `The existing "${sheetName}" sheet is protected and couldn't be cleared. ` +
              `Unprotect or delete that sheet, then save again.`,
          );
        }
      }

      // Non-fatal: throws when the workbook STRUCTURE is protected, and
      // a correct index in the wrong tab position beats aborting.
      try {
        sheet.position = 0;
        await context.sync();
      } catch (positionError) {
        console.warn(
          "Could not move the WI Index sheet to the first tab position " +
            "(workbook structure may be protected):",
          positionError,
        );
      }

      // Balance date arrives as an ISO "YYYY-MM-DD" string from the
      // Setup input; render in NZ short format so it matches how
      // preparers read dates elsewhere in the app.
      const balanceDateDisplay = info.balanceDate
        ? new Date(info.balanceDate).toLocaleDateString("en-NZ")
        : "";

      const data: string[][] = [
        ["Workflow Intelligence", ""],
        ["This workbook is using Workflow Intelligence.", ""],
        ["", ""],
        ["Client", info.clientName ?? ""],
        ["Balance Date", balanceDateDisplay],
        ["", ""],
        ["Preparer", info.preparerName ?? ""],
        ["Reviewer", info.reviewerName ?? ""],
        ["", ""],
        ["Workbook ID", info.spreadsheetId],
      ];

      const range = sheet.getRange("A1").getResizedRange(data.length - 1, 1);
      range.values = data;

      const titleRange = sheet.getRange("A1");
      titleRange.format.font.bold = true;
      titleRange.format.font.size = 16;

      sheet.getRange("A4:A5").format.font.bold = true;
      sheet.getRange("A7:A8").format.font.bold = true;
      sheet.getRange("A10").format.font.bold = true;

      // Workbook ID row muted so the UUID is visible but visually
      // subordinate to the human-facing values above it.
      sheet.getRange("A10:B10").format.font.color = "#9CA3AF";

      // Fresh WFI-generated sheet, so autofit is fine here (same
      // exception CLAUDE.md calls out for the completion certificate).
      sheet.getRange("A:B").format.autofitColumns();

      sheet.tabColor = "#9CA3AF";

      sheet.protection.protect({
        allowAutoFilter: false,
        allowDeleteColumns: false,
        allowDeleteRows: false,
        allowEditObjects: false,
        allowEditScenarios: false,
        allowFormatCells: false,
        allowFormatColumns: false,
        allowFormatRows: false,
        allowInsertColumns: false,
        allowInsertHyperlinks: false,
        allowInsertRows: false,
        allowPivotTables: false,
        allowSort: false,
      });

      await context.sync();
    });

    await ExcelService.save();
  } catch (error) {
    console.error("Error creating WI Index sheet:", error);
    throw new Error(`Failed to create WI Index sheet: ${error}`);
  }
}
